import { pb } from './pocketbase';

// Mapping des plans vers leurs limites mensuelles
const PLAN_LIMITS = {
    'Basic': 10,
    'Premium': 30,
    'Enterprise': 100,
    'Custom': 999999 // Illimité
};

/**
 * Récupère ou crée l'enregistrement de consommation pour un partenaire et un mois donné
 */
export const getOrCreateMonthlyConsumption = async (partnerId, year, month) => {
    try {
        // Chercher un enregistrement existant
        const existing = await pb.collection('partner_consumption').getList(1, 1, {
            filter: `partner_id = "${partnerId}" && year = ${year} && month = ${month}`
        });

        if (existing.items.length > 0) {
            return existing.items[0];
        }

        // Récupérer les infos du partenaire pour connaître son plan
        const partner = await pb.collection('partners').getOne(partnerId);
        const subscriptionPlan = partner.subscription_plan || 'Basic';
        const planLimit = PLAN_LIMITS[subscriptionPlan] || PLAN_LIMITS['Basic'];

        // Créer un nouvel enregistrement
        const newConsumption = await pb.collection('partner_consumption').create({
            partner_id: partnerId,
            year: year,
            month: month,
            folders_created: 0,
            plan_limit: planLimit,
            subscription_plan: subscriptionPlan
        });

        return newConsumption;
    } catch (error) {
        console.error('Erreur lors de la récupération/création de la consommation:', error);
        throw error;
    }
};

/**
 * Incrémente le compteur de dossiers créés pour le mois en cours
 */
export const incrementFolderCount = async (partnerId) => {
    try {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1; // JavaScript months are 0-indexed

        const consumption = await getOrCreateMonthlyConsumption(partnerId, year, month);

        // Incrémenter le compteur
        const updatedConsumption = await pb.collection('partner_consumption').update(consumption.id, {
            folders_created: consumption.folders_created + 1
        });

        return updatedConsumption;
    } catch (error) {
        console.error('Erreur lors de l\'incrémentation du compteur:', error);
        // Ne pas bloquer la création du dossier en cas d'erreur de tracking
        return null;
    }
};

/**
 * Récupère la consommation du mois en cours pour le partenaire connecté
 */
export const getCurrentMonthConsumption = async () => {
    try {
        if (!pb.authStore.isValid || !pb.authStore.model) {
            throw new Error('Utilisateur non authentifié');
        }

        const partnerId = pb.authStore.record.partner_id;
        if (!partnerId) {
            throw new Error('Aucun partenaire associé');
        }

        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;

        const consumption = await getOrCreateMonthlyConsumption(partnerId, year, month);

        return {
            success: true,
            data: {
                foldersCreated: consumption.folders_created,
                planLimit: consumption.plan_limit,
                subscriptionPlan: consumption.subscription_plan,
                year: consumption.year,
                month: consumption.month,
                percentageUsed: Math.round((consumption.folders_created / consumption.plan_limit) * 100),
                remaining: Math.max(0, consumption.plan_limit - consumption.folders_created)
            }
        };
    } catch (error) {
        console.error('Erreur lors de la récupération de la consommation:', error);
        return {
            success: false,
            error: error.message,
            data: null
        };
    }
};

/**
 * Récupère l'historique de consommation pour un partenaire
 */
export const getConsumptionHistory = async (partnerId = null, limit = 12) => {
    try {
        if (!pb.authStore.isValid || !pb.authStore.model) {
            throw new Error('Utilisateur non authentifié');
        }

        const targetPartnerId = partnerId || pb.authStore.record.partner_id;
        if (!targetPartnerId) {
            throw new Error('Aucun partenaire associé');
        }

        const history = await pb.collection('partner_consumption').getList(1, limit, {
            filter: `partner_id = "${targetPartnerId}"`,
            sort: '-year,-month'
        });

        return {
            success: true,
            data: history.items.map(item => ({
                year: item.year,
                month: item.month,
                foldersCreated: item.folders_created,
                planLimit: item.plan_limit,
                subscriptionPlan: item.subscription_plan,
                percentageUsed: Math.round((item.folders_created / item.plan_limit) * 100)
            }))
        };
    } catch (error) {
        console.error('Erreur lors de la récupération de l\'historique:', error);
        return {
            success: false,
            error: error.message,
            data: []
        };
    }
};

/**
 * Vérifie si le partenaire peut encore créer des dossiers ce mois-ci
 */
export const canCreateFolder = async () => {
    try {
        const result = await getCurrentMonthConsumption();
        
        if (!result.success) {
            // En cas d'erreur, on autorise par défaut
            return { canCreate: true, reason: null };
        }

        const { foldersCreated, planLimit, remaining } = result.data;

        if (foldersCreated >= planLimit) {
            return {
                canCreate: false,
                reason: `Limite mensuelle atteinte (${planLimit} dossiers). Passez à un forfait supérieur.`,
                consumption: result.data
            };
        }

        return {
            canCreate: true,
            reason: null,
            consumption: result.data
        };
    } catch (error) {
        console.error('Erreur lors de la vérification de la limite:', error);
        return { canCreate: true, reason: null };
    }
};

export const PLAN_LIMITS_CONFIG = PLAN_LIMITS;
